import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectAllFolders, selectAllProjects, selectAllReports, useGetAccountFolders, useGetAccountProjects, useGetAccountReports } from "fond/api";
import { useStarred } from "fond/projects/ItemMenu/hooks/useStarred";
import { ResourceEntity, Store } from "fond/types";

interface UseContentsReturn {
  items: ResourceEntity[];
  sharedItems: ResourceEntity[];
  starredItems: ResourceEntity[];
  /**
   * Indicates whether any of the content queries are still loading
   */
  isLoading: boolean;
  /**
   * Indicates whether any of the content queries are currently fetching but may have data from previous queries
   */
  isFetching: boolean;
}

const currentUserIsCreator = (currentUserName: string, item: ResourceEntity) => {
  if (item.EntityType === "report") {
    return item.Creator.Email === currentUserName;
  }
  return item.Creator === currentUserName;
};

/**
 * Gets all shared content to currently logged in user
 */
export const useContents = (): UseContentsReturn => {
  const { isFetching: isFoldersFetching, isLoading: isFoldersLoading } = useGetAccountFolders();
  const { isFetching: isProjectsFetching, isLoading: isProjectsLoading } = useGetAccountProjects();
  const { isFetching: isReportsFetching, isLoading: isReportsLoading } = useGetAccountReports();

  const isLoading = isFoldersLoading || isProjectsLoading || isReportsLoading;
  const isFetching = isFoldersFetching || isProjectsFetching || isReportsFetching;
  const folders = useSelector(selectAllFolders);
  const reports = useSelector(selectAllReports);
  const projects = useSelector(selectAllProjects);
  const currentUsername = useSelector((state: Store) => state.cognito.user?.username);
  const { starred } = useStarred();

  const sharedItems = useMemo(
    () =>
      [...folders, ...projects, ...reports].filter(
        (item) => !item.Permission?.Inherited && item.Permission?.IdentityType === "user" && !currentUserIsCreator(currentUsername, item)
      ),
    [currentUsername, folders, projects, reports]
  );

  const starredItems = [...folders, ...projects, ...reports].filter((item) => starred.includes(item.ID));

  return { items: [...folders, ...projects, ...reports], sharedItems, starredItems, isLoading, isFetching };
};
